import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Beaker, 
  Dna, 
  TestTube, 
  ArrowRight, 
  Calendar,
  CheckCircle,
  Clock,
  FileText
} from 'lucide-react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { listJobs } from '../../jobs/lib/jobs';
import type { JobWithResult } from '../../jobs/lib/jobs';

export function Homepage() {
  const { user } = useAuth();
  const [recentJobs, setRecentJobs] = useState<JobWithResult[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const jobs = await listJobs();
      
      // Get recent jobs (last 5)
      setRecentJobs(jobs.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'failed':
        return <div className="h-4 w-4 rounded-full bg-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getWorkflowIcon = (name: string) => {
    const lowercaseName = name.toLowerCase();
    if (lowercaseName.includes('enzyme')) return <Beaker className="h-5 w-5 text-blue-600" />;
    if (lowercaseName.includes('antibody')) return <Dna className="h-5 w-5 text-teal-600" />;
    if (lowercaseName.includes('peptide')) return <TestTube className="h-5 w-5 text-amber-600" />;
    return <FileText className="h-5 w-5 text-gray-600" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      const hours = Math.floor(diffInHours);
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 168) { // 7 days
      const days = Math.floor(diffInHours / 24);
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome back, {user?.email?.split('@')[0]}!
        </h1>
        <p className="text-gray-600">
          Here's an overview of your protein design projects.
        </p>
      </div>


      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/workflows/enzyme"
            className="bg-white rounded-sm border border-gray-200 p-6 hover:border-blue-500 hover:shadow-md transition-all group"
          >
            <div className="flex items-center justify-between mb-4">
              <Beaker className="h-8 w-8 text-blue-600" />
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Enzyme Design</h3>
            <p className="text-sm text-gray-600">Design enzymes for specific substrates</p>
          </Link>

          <Link 
            to="/workflows/antibody"
            className="bg-white rounded-sm border border-gray-200 p-6 hover:border-teal-500 hover:shadow-md transition-all group"
          >
            <div className="flex items-center justify-between mb-4">
              <Dna className="h-8 w-8 text-teal-600" />
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-teal-600 transition-colors" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Antibody Design</h3>
            <p className="text-sm text-gray-600">Create antibodies with specific binding</p>
          </Link>

          <Link 
            to="/workflows/peptide"
            className="bg-white rounded-sm border border-gray-200 p-6 hover:border-amber-500 hover:shadow-md transition-all group"
          >
            <div className="flex items-center justify-between mb-4">
              <TestTube className="h-8 w-8 text-amber-600" />
              <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-amber-600 transition-colors" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Peptide Design</h3>
            <p className="text-sm text-gray-600">Generate optimized peptide sequences</p>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
          <Link to="/jobs" className="text-sm text-blue-600 hover:text-blue-700">
            View all jobs →
          </Link>
        </div>

        {loading ? (
          <div className="bg-white rounded-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading recent activity...</p>
          </div>
        ) : recentJobs.length === 0 ? (
          <div className="bg-white rounded-sm border border-gray-200 p-8 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">No jobs yet</p>
            <Link 
              to="/workflows/enzyme" 
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Start your first job
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-200">
              {recentJobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/jobs/${job.id}`}
                  className="block p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getWorkflowIcon(job.name)}
                      <div>
                        <h3 className="font-medium text-gray-900">{job.name}</h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-gray-500">
                            <Calendar className="h-3 w-3 inline mr-1" />
                            {formatDate(job.created_at)}
                          </span>
                          <span className="text-sm text-gray-500">
                            SMILES: {job.smiles.substring(0, 20)}...
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(job.status)}
                      <span className={`text-sm font-medium capitalize ${
                        job.status === 'completed' ? 'text-green-600' :
                        job.status === 'running' ? 'text-blue-600' :
                        job.status === 'failed' ? 'text-red-600' :
                        'text-gray-600'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}